import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import dashboardService from './dashboardService';

const initialState = {
  isLoading: false,
  error: null,
  eligibiltyStep: 1,
  dealerType: "employee",
  dealerCode: "", // Made dynamic - no hardcoded value
  dashboardDetails: null,
  allDetails: null, // Add this for fetchAllDetails response
  referenceId: "",
  notifications: []
};

export const dashboardList = createAsyncThunk(
  "dashboard/dashboardList",
  async (data, thunkAPI) => {
    try {
      return await dashboardService.dashboardlist(data);
    } catch (error) {
      const message =
        error?.response?.data?.response_string ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue({ message, error });
    }
  }
);

export const fetchData = createAsyncThunk(
  "dashboard/fetchData",
  async (data, thunkAPI) => {
    try {
      return await dashboardService.fetchData(data);
    } catch (error) {
      const message =
        error?.response?.data?.response_string ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue({ message, error });
    }
  }
);

export const fetchAllDetails = createAsyncThunk(
  "dashboard/fetchAllDetails",
  async (data, thunkAPI) => {
    try {
      return await dashboardService.fetchAllDetails(data);
    } catch (error) {
      const message =
        error?.response?.data?.response_string ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue({ message, error });
    }
  }
);

export const sendSmsOtp = createAsyncThunk(
  "dashboard/sendOtp",
  async (data, thunkAPI) => {
    try {
      return await dashboardService.sendOtp(data);
    } catch (error) {
      const message =
        error?.response?.data?.response_string ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue({ message, error });
    }
  }
);

export const verifySmsOtp = createAsyncThunk(
  "dashboard/verifyOtp",
  async (data, thunkAPI) => {
    try {
      return await dashboardService.verifyOtp(data);
    } catch (error) {
      const message =
        error?.response?.data?.response_string ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue({ message, error });
    }
  }
);

export const verifyPanNumber = createAsyncThunk(
  "dashboard/verifyPan",
  async ({ data, encrypt = false }, thunkAPI) => {
    try {
      return await dashboardService.verifyPan(data, encrypt);
    } catch (error) {
      const message =
        error?.response?.data?.response_string ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue({ message, error });
    }
  }
);

export const resendSmsOtp = createAsyncThunk(
  "dashboard/resendOtp",
  async (data, thunkAPI) => {
    try {
      return await dashboardService.resendOtp(data);
    } catch (error) {
      const message =
        error?.response?.data?.response_string ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue({ message, error });
    }
  }
);

export const createApplication = createAsyncThunk(
  "dashboard/createApplication",
  async (data, thunkAPI) => {
    try {
      return await dashboardService.createApplication(data);
    } catch (error) {
      const message =
        error?.response?.data?.response_string ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue({ message, error });
    }
  }
);

export const getApplicationData = createAsyncThunk(
  "dashboard/getApplicationData",
  async (data, thunkAPI) => {
    try {
      return await dashboardService.getApplicationData(data);
    } catch (error) {
      const message =
        error?.response?.data?.response_string ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue({ message, error });
    }
  }
);

export const fetchNotification = createAsyncThunk(
  "dashboard/fetchNotification",
  async (data, thunkAPI) => {
    try {
      return await dashboardService.fetchNotification(data);
    } catch (error) {
      const message =
        error?.response?.data?.response_string ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue({ message, error });
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setEligibiltyStep: (state, action) => {
      state.eligibiltyStep = action.payload;
    },
    setDealerCode: (state, action) => {
      state.dealerCode = action.payload;
    },
    setDealerData: (state, action) => {
      state.dealerData = action.payload;
    },
    setDealerType: (state, action) => {
      state.dealerType = action.payload;
    },
    setSubStage: (state, action) => {
      state.subStage = action.payload;
    },
    setReferenceId: (state, action) => {
      state.referenceId = action.payload;
    },
    setApplicationDetail: (state, action) => {
      state.applicationDetail = action.payload;
    },
    clearDashboardData: (state) => {
      state.dashboardDetails = null;
      state.allDetails = null;
      state.error = null;
      state.dealerCode = ""; // Clear dealer code when clearing data
    },
    // New action to initialize dealer code from external sources
    initializeDealerCode: (state, action) => {
      if (!state.dealerCode && action.payload) {
        state.dealerCode = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardDetails = action.payload;
        // Dynamically set dealer code if available in response
        if (action.payload?.dealer_code && !state.dealerCode) {
          state.dealerCode = action.payload.dealer_code;
        }
      })
      .addCase(fetchData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allDetails = action.payload;
        state.dashboardDetails = action.payload;
        // Dynamically set dealer code if available in response
        if (action.payload?.dealer_code && !state.dealerCode) {
          state.dealerCode = action.payload.dealer_code;
        }
      })
      .addCase(fetchAllDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getApplicationData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getApplicationData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allDetails = action.payload;
        state.applicationDetail = action.payload?.response_data;
        // Update dealer code dynamically from response
        if (action.payload?.response_data?.dealer_code && action.payload?.response_data?.dealer_name) {
          state.dealerCode = action.payload.response_data.dealer_code + "-" + action.payload.response_data.dealer_name;
        }
      })
      .addCase(getApplicationData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchNotification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allDetails = action.payload;
        state.notifications = action.payload?.response_data ?? [];
      })
      .addCase(fetchNotification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
  },
});

export const {
  setEligibiltyStep, 
  setDealerCode, 
  setDealerData, 
  setDealerType, 
  setSubStage, 
  clearDashboardData, 
  setReferenceId,
  setApplicationDetail,
  initializeDealerCode
} = dashboardSlice.actions;

export default dashboardSlice.reducer;